import { useState, useEffect, useCallback } from 'react';
import { 
  signInAnonymously, 
  onAuthStateChanged, 
  User,
  signInWithPopup,
  linkWithPopup,
  linkWithCredential,
  signInWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  EmailAuthProvider
} from 'firebase/auth';
import { auth } from '../services/firebase';

interface UseAuthResult {
  user: User | null;
  loading: boolean;
  login: () => Promise<void>; // Anonymous login for backwards compatibility
  loginWithGoogle: () => Promise<void>;
  linkWithGoogle: () => Promise<void>;
  registerAndLinkEmail: (email: string, password: string) => Promise<void>;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

export function useAuth(): UseAuthResult {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Anonymous login
  const login = useCallback(async () => {
    try {
      await signInAnonymously(auth);
    } catch (error) {
      console.error('Anonymous login failed:', error);
      throw error;
    }
  }, []);

  // Google Sign-In
  const loginWithGoogle = useCallback(async () => {
    try {
      const provider = new GoogleAuthProvider();
      // Prompt selection even if already signed in to allow account switching
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error('Google login failed:', error);
      throw error;
    }
  }, []);

  // Link Anonymous to Google
  const linkWithGoogle = useCallback(async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Nessun utente corrente per effettuare il collegamento.');
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      await linkWithPopup(currentUser, provider);
    } catch (error) {
      console.error('Linking with Google failed:', error);
      throw error;
    }
  }, []);

  // Link Anonymous to Email/Password (Registration)
  const registerAndLinkEmail = useCallback(async (email: string, password: string) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Nessun utente corrente per effettuare il collegamento.');
      const credential = EmailAuthProvider.credential(email, password);
      await linkWithCredential(currentUser, credential);
    } catch (error) {
      console.error('Linking with Email failed:', error);
      throw error;
    }
  }, []);

  // Login with existing Email/Password
  const loginWithEmail = useCallback(async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Email login failed:', error);
      throw error;
    }
  }, []);

  // Logout
  const logout = useCallback(async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  }, []);

  return { 
    user, 
    loading, 
    login, 
    loginWithGoogle, 
    linkWithGoogle, 
    registerAndLinkEmail, 
    loginWithEmail, 
    logout 
  };
}